import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Mail, Clock, CheckCircle, Building2 } from 'lucide-react';

const PlatformMessages = () => {
  const { apiFetch } = usePlatformAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = async () => {
    try {
      const res = await apiFetch('/messages');
      setMessages(res.data.messages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  const markRead = async (id) => {
    try {
      await apiFetch(`/messages/${id}/read`, { method: 'PATCH' });
      setMessages(prev => prev.map(m => m.message_id === id ? { ...m, read: true } : m));
      toast.success('Marked as read');
    } catch (err) {
      toast.error('Failed');
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="platform-messages">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Contact Messages</h1>
        <p className="text-slate-400">{messages.length} message(s) from schools and visitors</p>
      </div>

      {messages.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-12 text-center">
            <Mail className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No messages yet. They'll appear here when visitors use the contact form.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {messages.map((msg) => (
            <Card key={msg.message_id} className={`bg-slate-900 border-slate-800 ${!msg.read ? 'border-l-4 border-l-indigo-500' : ''}`} data-testid={`message-${msg.message_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-semibold">{msg.name}</h3>
                      {!msg.read && <span className="h-2 w-2 rounded-full bg-indigo-500" />}
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(msg.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400 mb-3">
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{msg.email}</span>
                      {msg.phone && <span>{msg.phone}</span>}
                      {msg.school && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{msg.school}</span>}
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">{msg.message}</p>
                  </div>
                  {!msg.read && (
                    <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800 flex-shrink-0" onClick={() => markRead(msg.message_id)} data-testid={`mark-read-${msg.message_id}`}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Read
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlatformMessages;
